import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const MIN_TARGET = 1.01;
const MAX_TARGET = 1000000;

const LimboPage = () => {
  const navigate = useNavigate();
  const [isUser, setIsUser] = useState(false);
  const [userCoins, setUserCoins] = useState(0);
  const [settings, setSettings] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);

  const [betAmount, setBetAmount] = useState(1);
  const [targetMultiplier, setTargetMultiplier] = useState(2);
  const [winChance, setWinChance] = useState(49.5);
  const [targetInput, setTargetInput] = useState('2.00');
  const [chanceInput, setChanceInput] = useState('49.50');

  const [displayValue, setDisplayValue] = useState(1);
  const [lastOutcome, setLastOutcome] = useState(null); // 'win' | 'lose' | null
  const [animating, setAnimating] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [history, setHistory] = useState([]);
  const animRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    setIsUser(!!token);
    if (!token) {
      setTimeout(() => navigate('/login'), 1500);
      return;
    }
    axios.get('/api/games/settings/limbo').then((r) => setSettings(r.data)).catch(() => {});
    axios.get('/api/users/me/coins', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUserCoins(r.data.coins || 0)).catch(() => {});
    axios.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEmailVerified(r.data.emailVerified || r.data.email_verified || false)).catch(() => {});
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [navigate]);

  const houseEdge = settings ? Math.min(0.5, Math.max(0, 1 - (settings.rtp ?? 0.99))) : 0.01;
  const maxChance = Math.round(((1 - houseEdge) / MIN_TARGET) * 10000) / 100;

  const applyTarget = (rawValue) => {
    setTargetInput(rawValue);
    let t = parseFloat(rawValue.replace(',', '.'));
    if (isNaN(t)) return;
    t = Math.min(MAX_TARGET, Math.max(MIN_TARGET, t));
    const chance = ((1 - houseEdge) / t) * 100;
    setTargetMultiplier(Math.round(t * 100) / 100);
    setWinChance(Math.round(chance * 100) / 100);
    setChanceInput((Math.round(chance * 100) / 100).toFixed(2));
  };

  const applyChance = (rawValue) => {
    setChanceInput(rawValue);
    let c = parseFloat(rawValue.replace(',', '.'));
    if (isNaN(c)) return;
    c = Math.min(maxChance, Math.max(0.0001, c));
    const t = (1 - houseEdge) / (c / 100);
    const clampedT = Math.min(MAX_TARGET, Math.max(MIN_TARGET, t));
    setTargetMultiplier(Math.round(clampedT * 100) / 100);
    setWinChance(Math.round(c * 100) / 100);
    setTargetInput((Math.round(clampedT * 100) / 100).toFixed(2));
  };

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-dark">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  const minBet = settings.minBet || settings.min_bet || 1;
  const maxBet = settings.maxBet || settings.max_bet || 100;
  const maxWin = settings.maxWin ?? settings.max_win ?? null;
  const rawWinOnTarget = Math.floor(betAmount * targetMultiplier);
  const cappedWinOnTarget = maxWin ? Math.min(rawWinOnTarget, maxWin) : rawWinOnTarget;
  const profitOnWin = Math.max(0, cappedWinOnTarget - betAmount);
  const profitIsCapped = maxWin && rawWinOnTarget > maxWin;

  const clampBet = (value) => {
    let v = parseInt(value) || minBet;
    if (v < minBet) v = minBet;
    if (v > maxBet) v = maxBet;
    if (v > userCoins) v = userCoins;
    return v;
  };

  const animateTo = (resultMultiplier, win) => {
    setAnimating(true);
    setLastOutcome(null);
    const start = performance.now();
    const duration = 900;
    const cap = Math.min(resultMultiplier, Math.max(targetMultiplier * 1.4, resultMultiplier));
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayValue(1 + (cap - 1) * eased);
      if (t < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setDisplayValue(resultMultiplier);
        setAnimating(false);
        setLastOutcome(win ? 'win' : 'lose');
      }
    };
    animRef.current = requestAnimationFrame(step);
  };

  const play = async () => {
    if (!isUser) { navigate('/login'); return; }
    if (!emailVerified) { alert('Oyun oynamak için e-posta adresinizi doğrulamanız gerekmektedir.'); return; }
    if (betAmount > userCoins) { alert('Yetersiz puan!'); return; }
    if (playing || animating) return;

    setPlaying(true);
    setLastOutcome(null);
    try {
      const token = localStorage.getItem('userToken');
      const { data } = await axios.post('/api/games/limbo/play', { betAmount, targetMultiplier }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      userData.coins = data.newBalance;
      localStorage.setItem('userData', JSON.stringify(userData));
      window.dispatchEvent(new Event('userTokenChanged'));
      setUserCoins(data.newBalance);

      animateTo(data.resultMultiplier, data.win);
      setHistory((prev) => [{ value: data.resultMultiplier, win: data.win }, ...prev].slice(0, 14));
    } catch (error) {
      alert(error.response?.data?.error || 'Oyun oynanamadı');
    } finally {
      setPlaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-dark py-4 sm:py-8 px-2 sm:px-4 overflow-x-hidden">
      <style>{`
        @keyframes limbo-glow {
          0%, 100% { text-shadow: 0 0 20px rgba(212,175,55,0.4); }
          50% { text-shadow: 0 0 40px rgba(212,175,55,0.7); }
        }
        .limbo-number { animation: limbo-glow 2s ease-in-out infinite; }
        .limbo-number.win { animation: none; text-shadow: 0 0 35px rgba(16,185,129,0.7); }
        .limbo-number.lose { animation: none; text-shadow: 0 0 35px rgba(239,68,68,0.6); }
      `}</style>

      <div className="max-w-6xl mx-auto w-full">
        <div className="mb-4 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => navigate('/games')}
            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 shadow-sm backdrop-blur-md transition hover:border-gold/40 hover:bg-white/10 hover:text-gold"
          >
            <span className="text-lg leading-none">‹</span>
            <span>Saguex Originals</span>
          </button>
          <div className="pulse-glow w-full rounded-2xl border border-gold/35 bg-gradient-to-br from-gold/15 to-amber-900/20 px-5 py-3 shadow-lg backdrop-blur-sm sm:w-auto">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold/80">Bakiye</div>
            <div className="text-center font-bold tabular-nums text-gold sm:text-left text-lg sm:text-xl">
              {userCoins.toLocaleString('tr-TR')} <span className="text-sm font-semibold text-gold/70">Sagu Coin</span>
            </div>
          </div>
        </div>

        {isUser && !emailVerified && (
          <div className="bg-yellow-500/20 border-2 border-yellow-500/50 rounded-xl p-4 mb-6">
            <p className="text-yellow-400 font-semibold">⚠️ Oyun oynamak için e-posta adresinizi doğrulamanız gerekmektedir.</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
          {/* Control Panel */}
          <div className="order-2 lg:order-1 lg:col-span-1">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-navy-800/90 to-navy-900/95 p-4 shadow-2xl shadow-black/40 backdrop-blur-md sm:rounded-3xl sm:p-6 ring-1 ring-gold/20">
              <div className="mb-5">
                <label className="mb-2 block text-center text-[11px] font-semibold uppercase tracking-widest text-white/45">
                  Bahis Tutarı (Sagu Coin)
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(clampBet(e.target.value))}
                    className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-center text-lg font-semibold tabular-nums text-white shadow-inner outline-none ring-gold/30 transition focus:border-gold/50 focus:ring-2"
                    min={minBet}
                    max={Math.min(maxBet, userCoins)}
                  />
                  <div className="flex shrink-0 flex-col gap-2">
                    <button type="button" onClick={() => setBetAmount(clampBet(Math.floor(betAmount / 2)))} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10">÷2</button>
                    <button type="button" onClick={() => setBetAmount(clampBet(betAmount * 2))} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10">×2</button>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => setBetAmount(minBet)} className="flex-1 rounded-xl border border-gold/25 bg-gold/10 py-2.5 text-xs font-semibold text-gold transition hover:bg-gold/20">Min</button>
                  <button type="button" onClick={() => setBetAmount(clampBet(maxBet))} className="flex-1 rounded-xl border border-gold/25 bg-gold/10 py-2.5 text-xs font-semibold text-gold transition hover:bg-gold/20">Max</button>
                </div>
              </div>

              <button
                type="button"
                onClick={play}
                disabled={playing || animating || betAmount > userCoins || betAmount < minBet}
                className="w-full rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-emerald-900/40 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {playing || animating ? 'Oynatılıyor…' : 'Bahis yap'}
              </button>
              {betAmount > userCoins && <p className="mt-3 text-center text-sm text-red-400/90">Yetersiz puan</p>}

              <div className="mt-5 rounded-2xl border border-white/10 bg-black/25 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-white/45">Kazanıldığında Kâr</div>
                <div className="mt-1 flex items-baseline justify-between gap-2">
                  <span className="text-2xl font-bold tabular-nums text-gold">{profitOnWin}</span>
                  <span className="text-sm text-white/50">Sagu Coin</span>
                </div>
                {profitIsCapped && (
                  <div className="mt-1.5 text-[11px] text-amber-400/80">Maksimum kazanç sınırına ulaşıldı ({maxWin.toLocaleString('tr-TR')} Sagu Coin)</div>
                )}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-widest text-white/45">Hedef Çarpan</label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={targetInput}
                      onChange={(e) => applyTarget(e.target.value)}
                      onBlur={() => setTargetInput(targetMultiplier.toFixed(2))}
                      className="w-full rounded-xl border border-white/10 bg-black/35 px-3 py-2.5 text-center text-sm font-semibold tabular-nums text-white outline-none ring-gold/30 transition focus:border-gold/50 focus:ring-2"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40">×</span>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-widest text-white/45">Kazanma Şansı</label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={chanceInput}
                      onChange={(e) => applyChance(e.target.value)}
                      onBlur={() => setChanceInput(winChance.toFixed(2))}
                      className="w-full rounded-xl border border-white/10 bg-black/35 px-3 py-2.5 text-center text-sm font-semibold tabular-nums text-white outline-none ring-gold/30 transition focus:border-gold/50 focus:ring-2"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40">%</span>
                  </div>
                </div>
              </div>
              <input
                type="range"
                min={MIN_TARGET}
                max={100}
                step={0.01}
                value={Math.min(100, targetMultiplier)}
                onChange={(e) => applyTarget(e.target.value)}
                className="mt-4 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-gold"
              />
            </div>
          </div>

          {/* Display */}
          <div className="order-1 lg:order-2 lg:col-span-3">
            <div
              className="relative flex min-h-[420px] flex-col items-center justify-center overflow-hidden rounded-3xl border-2 border-gold/45 p-4 shadow-2xl shadow-black/50 sm:p-6 lg:p-8"
              style={{
                backgroundColor: '#0d1b3b',
                backgroundImage: `
                  radial-gradient(ellipse 120% 80% at 50% -20%, rgba(212, 175, 55, 0.12), transparent 55%),
                  radial-gradient(ellipse 90% 60% at 80% 100%, rgba(0, 0, 0, 0.35), transparent 50%)
                `
              }}
            >
              <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

              {history.length === 0 && (
                <div className="mb-6 flex justify-center">
                  <img
                    src="/images/originals/limbo.png"
                    alt="Limbo"
                    className="h-36 sm:h-44 w-auto rounded-xl object-contain shadow-[0_0_40px_rgba(212,175,55,0.25)]"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              {history.length > 0 && (
                <div className="mb-8 flex max-w-full flex-wrap justify-center gap-2 overflow-hidden">
                  {history.map((h, i) => (
                    <span
                      key={i}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-bold tabular-nums ${h.win ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}
                    >
                      {h.value.toFixed(2)}×
                    </span>
                  ))}
                </div>
              )}

              <div
                className={`limbo-number font-extrabold tabular-nums tracking-tight ${lastOutcome === 'win' ? 'win text-emerald-400' : lastOutcome === 'lose' ? 'lose text-red-400' : 'text-gold'}`}
                style={{ fontSize: 'clamp(2.75rem, 9vw, 6rem)' }}
              >
                {displayValue.toFixed(2)}×
              </div>

              {lastOutcome && (
                <div className={`mt-4 rounded-xl px-6 py-2 text-sm font-bold uppercase tracking-wide ${lastOutcome === 'win' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {lastOutcome === 'win' ? 'Kazandınız!' : 'Kaybettiniz'}
                </div>
              )}

              <div className="mt-6 text-center text-xs text-white/40">
                Hedef <span className="font-semibold text-white/70">{targetMultiplier.toFixed(2)}×</span> ve üzeri gelirse kazanırsınız
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LimboPage;
